import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format, differenceInDays, parseISO, isWithinInterval } from "date-fns";
import {
  Calendar as CalendarIcon,
  Plus,
  Minus,
  Loader2,
  CheckCircle,
  AlertCircle,
  PartyPopper,
  Clock,
  Palmtree,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_DELIVERY_PICKUP_FEE = 20;

// Cross-browser compatible time picker component
const TimePicker = ({ value, onChange, id, className, "data-testid": testId }) => {
  // Parse existing value or default to empty
  const parseTime = (timeStr) => {
    if (!timeStr) return { hour: "", minute: "", period: "AM" };
    const [h, m] = timeStr.split(":");
    const hour24 = parseInt(h, 10);
    const period = hour24 >= 12 ? "PM" : "AM";
    let hour12 = hour24 % 12;
    if (hour12 === 0) hour12 = 12;
    return { hour: hour12.toString(), minute: m || "00", period };
  };

  const { hour, minute, period } = parseTime(value);

  const handleChange = (newHour, newMinute, newPeriod) => {
    if (!newHour || !newMinute) {
      onChange("");
      return;
    }
    let hour24 = parseInt(newHour, 10);
    if (newPeriod === "PM" && hour24 !== 12) hour24 += 12;
    if (newPeriod === "AM" && hour24 === 12) hour24 = 0;
    const timeStr = `${hour24.toString().padStart(2, "0")}:${newMinute}`;
    onChange(timeStr);
  };

  // Generate hour options (1-12)
  const hours = Array.from({ length: 12 }, (_, i) => (i + 1).toString());
  // Generate minute options (00, 15, 30, 45 for simplicity, or 00-59)
  const minutes = ["00", "15", "30", "45"];

  return (
    <div className={cn("flex gap-2 items-center", className)} id={id} data-testid={testId}>
      <Select value={hour} onValueChange={(h) => handleChange(h, minute || "00", period)}>
        <SelectTrigger className="w-20 h-12 rounded-xl">
          <SelectValue placeholder="Hr" />
        </SelectTrigger>
        <SelectContent>
          {hours.map((h) => (
            <SelectItem key={h} value={h}>{h}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-charcoal/60 font-bold">:</span>
      <Select value={minute} onValueChange={(m) => handleChange(hour || "12", m, period)}>
        <SelectTrigger className="w-20 h-12 rounded-xl">
          <SelectValue placeholder="Min" />
        </SelectTrigger>
        <SelectContent>
          {minutes.map((m) => (
            <SelectItem key={m} value={m}>{m}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={period} onValueChange={(p) => handleChange(hour || "12", minute || "00", p)}>
        <SelectTrigger className="w-20 h-12 rounded-xl">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="AM">AM</SelectItem>
          <SelectItem value="PM">PM</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};

const Booking = () => {
  const { language, t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [deliveryFee, setDeliveryFee] = useState(DEFAULT_DELIVERY_PICKUP_FEE);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Form state
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [availability, setAvailability] = useState({});
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    notes: "",
  });
  // Logistics time fields
  const [eventStartTime, setEventStartTime] = useState("");
  const [eventEndTime, setEventEndTime] = useState("");
  const [deliveryEarliest, setDeliveryEarliest] = useState("");
  const [pickupLatest, setPickupLatest] = useState("");
  
  // Validation errors
  const [errors, setErrors] = useState({});

  // Vacation mode state
  const [vacation, setVacation] = useState({ enabled: false, start_date: null, end_date: null });

  // Get translated product name - fallback to English if Spanish is empty
  const getProductName = (product) => {
    if (language === "es" && product.name_es) {
      return product.name_es;
    }
    return product.name;
  };

  useEffect(() => {
    fetchProducts();
    fetchVacationSettings();
    
    // Refresh vacation settings every 30 seconds to catch admin changes
    const vacationInterval = setInterval(fetchVacationSettings, 30000);
    return () => clearInterval(vacationInterval);
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      checkAllAvailability();
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (submitted) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [submitted]);

  const fetchVacationSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/vacation`);
      setVacation(response.data);
    } catch (error) {
      console.error("Failed to load vacation settings");
    }
  };

  // Check if a date falls within the vacation period
  const isDateInVacation = (date) => {
    if (!vacation.enabled || !vacation.start_date || !vacation.end_date) {
      return false;
    }
    const vacStart = parseISO(vacation.start_date);
    const vacEnd = parseISO(vacation.end_date);
    return isWithinInterval(date, { start: vacStart, end: vacEnd });
  };

  // Check if the selected date range overlaps with vacation
  const doesRangeOverlapVacation = (start, end) => {
    if (!vacation.enabled || !vacation.start_date || !vacation.end_date || !start || !end) {
      return false;
    }
    const vacStart = parseISO(vacation.start_date);
    const vacEnd = parseISO(vacation.end_date);
    // Overlap occurs if: booking starts before vacation ends AND booking ends after vacation starts
    return start <= vacEnd && end >= vacStart;
  };

  const fetchProducts = async () => {
    try {
      const [productsResponse, homepageResponse] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/settings/homepage`),
      ]);
      setProducts(productsResponse.data);
      setDeliveryFee(homepageResponse.data?.delivery_pickup_fee ?? DEFAULT_DELIVERY_PICKUP_FEE);
    } catch (error) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const checkAllAvailability = async () => {
    if (!startDate || !endDate) return;

    const startStr = format(startDate, "yyyy-MM-dd");
    const endStr = format(endDate, "yyyy-MM-dd");

    const newAvailability = {};
    for (const product of products) {
      try {
        const response = await axios.get(
          `${API}/products/${product.id}/availability?start_date=${startStr}&end_date=${endStr}`
        );
        newAvailability[product.id] = response.data.available_quantity;
      } catch (error) {
        newAvailability[product.id] = 0;
      }
    }
    setAvailability(newAvailability);
  };

  const updateQuantity = (productId, delta) => {
    const product = products.find((p) => p.id === productId);
    const maxAvailable = availability[productId] ?? product.total_inventory;
    const currentQty = selectedItems[productId] || 0;
    const newQty = Math.max(0, Math.min(maxAvailable, currentQty + delta));

    setSelectedItems((prev) => ({
      ...prev,
      [productId]: newQty,
    }));
  };

  const calculateTotal = () => {
    if (!startDate || !endDate) return 0;
    const days = Math.max(1, differenceInDays(endDate, startDate) + 1);

    const itemsTotal = Object.entries(selectedItems).reduce((total, [productId, qty]) => {
      const product = products.find((p) => p.id === productId);
      return total + (product ? product.price_per_day * qty * days : 0);
    }, 0);

    if (getSelectedItemsCount() > 0) {
      return itemsTotal + Number(deliveryFee || 0);
    }

    return itemsTotal;
  };

  const getRentalDays = () => {
    if (!startDate || !endDate) return 0;
    return Math.max(1, differenceInDays(endDate, startDate) + 1);
  };

  const getSelectedItemsCount = () => {
    return Object.values(selectedItems).reduce((sum, qty) => sum + qty, 0);
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Date validation
    if (!startDate) newErrors.startDate = t.booking.required;
    if (!endDate) newErrors.endDate = t.booking.required;
    
    // Items validation
    if (getSelectedItemsCount() === 0) newErrors.items = t.booking.required;
    
    // Customer info validation
    if (!customerInfo.name.trim()) newErrors.name = t.booking.required;
    if (!customerInfo.email.trim()) newErrors.email = t.booking.required;
    if (!customerInfo.phone.trim()) newErrors.phone = t.booking.required;
    if (!customerInfo.address.trim()) newErrors.address = t.booking.required;
    
    // Logistics time validation
    if (!eventStartTime) newErrors.eventStartTime = t.booking.required;
    if (!eventEndTime) newErrors.eventEndTime = t.booking.required;
    if (!deliveryEarliest) newErrors.deliveryEarliest = t.booking.required;
    if (!pickupLatest) newErrors.pickupLatest = t.booking.required;
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors({});

    // Validate all fields
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      // Scroll to first error
      const firstErrorField = document.querySelector('.border-red-500');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setSubmitting(true);

    try {
      // Re-fetch vacation settings right before submission to catch any recent changes
      const vacationResponse = await axios.get(`${API}/settings/vacation`);
      const currentVacation = vacationResponse.data;
      
      // Client-side vacation overlap check (server will also validate)
      if (currentVacation.enabled && currentVacation.start_date && currentVacation.end_date) {
        const bookingStart = format(startDate, "yyyy-MM-dd");
        const bookingEnd = format(endDate, "yyyy-MM-dd");
        const vacStart = currentVacation.start_date;
        const vacEnd = currentVacation.end_date;
        
        // Overlap check: booking starts before vacation ends AND booking ends after vacation starts
        if (bookingStart <= vacEnd && bookingEnd >= vacStart) {
          toast.error(language === "es" 
            ? `No disponible del ${vacStart} al ${vacEnd}. Por favor seleccione otras fechas.`
            : `We are unavailable from ${vacStart} to ${vacEnd}. Please select different dates.`
          );
          setVacation(currentVacation); // Update state with latest
          setSubmitting(false);
          return;
        }
      }

      const items = Object.entries(selectedItems)
        .filter(([, qty]) => qty > 0)
        .map(([productId, qty]) => {
          const product = products.find((p) => p.id === productId);
          return {
            product_id: productId,
            product_name: product.name, // Always store English name in database for consistency
            quantity: qty,
            price_per_day: product.price_per_day,
          };
        });

      await axios.post(`${API}/bookings`, {
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        customer_phone: customerInfo.phone,
        delivery_address: customerInfo.address,
        start_date: format(startDate, "yyyy-MM-dd"),
        end_date: format(endDate, "yyyy-MM-dd"),
        event_start_time: eventStartTime,
        event_end_time: eventEndTime,
        delivery_earliest: deliveryEarliest,
        pickup_latest: pickupLatest,
        items,
        notes: customerInfo.notes || null,
      });

      setSubmitted(true);
      setErrors({});
      toast.success("Booking submitted successfully!");
    } catch (error) {
      const message =
        error.response?.data?.detail || "Failed to submit booking";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sage" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center py-20">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <PartyPopper className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="font-heading font-bold text-3xl text-charcoal mb-4">
            {t.booking.successTitle}
          </h1>
          <p className="text-charcoal/70 mb-6">
            {t.booking.successMessage}
          </p>
          <Button
            data-testid="new-booking-btn"
            onClick={() => {
              setSubmitted(false);
              setSelectedItems({});
              setStartDate(null);
              setEndDate(null);
              setCustomerInfo({ name: "", email: "", phone: "", address: "", notes: "" });
              setEventStartTime("");
              setEventEndTime("");
              setDeliveryEarliest("");
              setPickupLatest("");
              setErrors({});
            }}
            className="bg-sage hover:bg-sage/90 text-white rounded-full px-6"
          >
            {t.booking.bookAnother}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 lg:py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="font-heading font-bold text-4xl sm:text-5xl text-charcoal mb-4">
            {t.booking.title}
          </h1>
          <p className="text-lg text-charcoal/70 max-w-2xl">
            {t.booking.subtitle}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Date Selection & Products */}
            <div className="lg:col-span-2 space-y-8">
              {/* Date Selection */}
              <div className="bg-white rounded-3xl border border-charcoal/10 p-6 shadow-sm">
                <h2 className="font-heading font-bold text-xl text-charcoal mb-6">
                  1. {t.booking.rentalPeriod}
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-charcoal/70 mb-2 block">{t.booking.startDate} *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          data-testid="start-date-picker"
                          className={cn(
                            "w-full justify-start text-left font-normal h-12 rounded-xl",
                            !startDate && "text-charcoal/50",
                            errors.startDate && "border-red-500 border-2"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {startDate ? format(startDate, "PPP") : t.booking.startDate}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={startDate}
                          onSelect={(date) => {
                            setStartDate(date);
                            setErrors(prev => ({ ...prev, startDate: null }));
                            if (date && (!endDate || endDate < date)) {
                              // Default to same-day rental unless customer selects a different end date
                              setEndDate(date);
                            }
                          }}
                          disabled={(date) => date < new Date() || isDateInVacation(date)}
                          modifiers={{
                            vacation: vacation.enabled && vacation.start_date && vacation.end_date
                              ? { from: parseISO(vacation.start_date), to: parseISO(vacation.end_date) }
                              : undefined
                          }}
                          modifiersStyles={{
                            vacation: { backgroundColor: '#dbeafe', color: '#1d4ed8', textDecoration: 'line-through' }
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    {errors.startDate && <p className="text-red-500 text-sm mt-1">{errors.startDate}</p>}
                  </div>
                  <div>
                    <Label className="text-charcoal/70 mb-2 block">{t.booking.endDate} *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          data-testid="end-date-picker"
                          className={cn(
                            "w-full justify-start text-left font-normal h-12 rounded-xl",
                            !endDate && "text-charcoal/50",
                            errors.endDate && "border-red-500 border-2"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {endDate ? format(endDate, "PPP") : t.booking.endDate}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={endDate}
                          onSelect={(date) => {
                            setEndDate(date);
                            setErrors(prev => ({ ...prev, endDate: null }));
                          }}
                          disabled={(date) =>
                            date < new Date() || (startDate && date < startDate) || isDateInVacation(date)
                          }
                          modifiers={{
                            vacation: vacation.enabled && vacation.start_date && vacation.end_date
                              ? { from: parseISO(vacation.start_date), to: parseISO(vacation.end_date) }
                              : undefined
                          }}
                          modifiersStyles={{
                            vacation: { backgroundColor: '#dbeafe', color: '#1d4ed8', textDecoration: 'line-through' }
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    {errors.endDate && <p className="text-red-500 text-sm mt-1">{errors.endDate}</p>}
                  </div>
                </div>
                {/* Vacation Mode Warning */}
                {vacation.enabled && vacation.start_date && vacation.end_date && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-center gap-2">
                    <Palmtree className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span className="text-blue-700 text-sm">
                      {language === "es" 
                        ? `No disponible del ${vacation.start_date} al ${vacation.end_date}`
                        : `Unavailable from ${vacation.start_date} to ${vacation.end_date}`
                      }
                    </span>
                  </div>
                )}
                {startDate && endDate && (
                  <div className="mt-4 p-3 bg-sage/5 rounded-xl flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-sage" />
                    <span className="text-sage font-medium">
                      {getRentalDays()} {t.booking.rentalDays}
                    </span>
                  </div>
                )}
              </div>

              {/* Product Selection */}
              <div className="bg-white rounded-3xl border border-charcoal/10 p-6 shadow-sm">
                <h2 className="font-heading font-bold text-xl text-charcoal mb-6">
                  2. {t.booking.selectItems}
                </h2>
                {!startDate || !endDate ? (
                  <div className="text-center py-8 text-charcoal/60">
                    <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>{t.booking.selectDates}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {products.map((product) => {
                      const availableQty =
                        availability[product.id] ?? product.total_inventory;
                      const selectedQty = selectedItems[product.id] || 0;
                      const isUnavailable = availableQty === 0;

                      return (
                        <div
                          key={product.id}
                          data-testid={`booking-product-${product.id}`}
                          className={cn(
                            "flex items-center gap-4 p-4 rounded-2xl border transition-colors",
                            isUnavailable
                              ? "border-charcoal/15 bg-cream opacity-60"
                              : selectedQty > 0
                              ? "border-sage/30 bg-sage/5"
                              : "border-charcoal/15 hover:border-slate-300"
                          )}
                        >
                          <img
                            src={product.image_url}
                            alt={getProductName(product)}
                            className="w-20 h-20 object-cover rounded-xl"
                          />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-heading font-bold text-charcoal">
                              {getProductName(product)}
                            </h3>
                            <p className="text-sm text-charcoal/60 truncate">
                              ${product.price_per_day}/day
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              {isUnavailable ? (
                                <span className="text-sm text-red-500 flex items-center gap-1">
                                  <AlertCircle className="w-4 h-4" />
                                  Unavailable for these dates
                                </span>
                              ) : (
                                <span className="text-sm text-green-600">
                                  {availableQty} available
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              data-testid={`qty-minus-${product.id}`}
                              className="h-10 w-10 rounded-full"
                              disabled={isUnavailable || selectedQty === 0}
                              onClick={() => updateQuantity(product.id, -1)}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span
                              className="w-12 text-center font-bold text-lg"
                              data-testid={`qty-value-${product.id}`}
                            >
                              {selectedQty}
                            </span>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              data-testid={`qty-plus-${product.id}`}
                              className="h-10 w-10 rounded-full"
                              disabled={isUnavailable || selectedQty >= availableQty}
                              onClick={() => updateQuantity(product.id, 1)}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Customer Information */}
              <div className="bg-white rounded-3xl border border-charcoal/10 p-6 shadow-sm">
                <h2 className="font-heading font-bold text-xl text-charcoal mb-6">
                  3. {t.booking.customerInfo}
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-charcoal/70 mb-2 block">
                      {t.booking.name} *
                    </Label>
                    <Input
                      id="name"
                      data-testid="customer-name"
                      value={customerInfo.name}
                      onChange={(e) => {
                        setCustomerInfo((prev) => ({ ...prev, name: e.target.value }));
                        setErrors(prev => ({ ...prev, name: null }));
                      }}
                      placeholder={t.booking.namePlaceholder}
                      className={cn("h-12 rounded-xl", errors.name && "border-red-500 border-2")}
                      required
                    />
                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-charcoal/70 mb-2 block">
                      {t.booking.email} *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="customer-email"
                      value={customerInfo.email}
                      onChange={(e) => {
                        setCustomerInfo((prev) => ({ ...prev, email: e.target.value }));
                        setErrors(prev => ({ ...prev, email: null }));
                      }}
                      placeholder={t.booking.emailPlaceholder}
                      className={cn("h-12 rounded-xl", errors.email && "border-red-500 border-2")}
                      required
                    />
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-charcoal/70 mb-2 block">
                      {t.booking.phone} *
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      data-testid="customer-phone"
                      value={customerInfo.phone}
                      onChange={(e) => {
                        setCustomerInfo((prev) => ({ ...prev, phone: e.target.value }));
                        setErrors(prev => ({ ...prev, phone: null }));
                      }}
                      placeholder={t.booking.phonePlaceholder}
                      className={cn("h-12 rounded-xl", errors.phone && "border-red-500 border-2")}
                      required
                    />
                    {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="address" className="text-charcoal/70 mb-2 block">
                      {t.booking.deliveryAddress} *
                    </Label>
                    <Textarea
                      id="address"
                      data-testid="customer-address"
                      value={customerInfo.address}
                      onChange={(e) => {
                        setCustomerInfo((prev) => ({ ...prev, address: e.target.value }));
                        setErrors(prev => ({ ...prev, address: null }));
                      }}
                      placeholder={t.booking.deliveryAddressPlaceholder}
                      className={cn("rounded-xl resize-none", errors.address && "border-red-500 border-2")}
                      rows={2}
                      required
                    />
                    {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="notes" className="text-charcoal/70 mb-2 block">
                      {t.booking.specialRequests}
                    </Label>
                    <Textarea
                      id="notes"
                      data-testid="customer-notes"
                      value={customerInfo.notes}
                      onChange={(e) =>
                        setCustomerInfo((prev) => ({
                          ...prev,
                          notes: e.target.value,
                        }))
                      }
                      placeholder={t.booking.specialRequestsPlaceholder}
                      className="rounded-xl resize-none"
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Event & Logistics Schedule */}
              <div className="bg-white rounded-3xl border border-charcoal/10 p-6 shadow-sm">
                <h2 className="font-heading font-bold text-xl text-charcoal mb-2">
                  4. {t.booking.eventSchedule}
                </h2>
                <p className="text-charcoal/60 text-sm mb-6">{t.booking.logistics}</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="eventStartTime" className="text-charcoal/70 mb-2 block">
                      <Clock className="w-4 h-4 inline mr-1" />
                      {t.booking.eventStart} *
                    </Label>
                    <TimePicker
                      id="eventStartTime"
                      data-testid="event-start-time"
                      value={eventStartTime}
                      onChange={(val) => {
                        setEventStartTime(val);
                        setErrors(prev => ({ ...prev, eventStartTime: null }));
                      }}
                      className={cn(errors.eventStartTime && "[&_button]:border-red-500 [&_button]:border-2")}
                    />
                    {errors.eventStartTime && <p className="text-red-500 text-sm mt-1">{errors.eventStartTime}</p>}
                  </div>
                  <div>
                    <Label htmlFor="eventEndTime" className="text-charcoal/70 mb-2 block">
                      <Clock className="w-4 h-4 inline mr-1" />
                      {t.booking.eventEnd} *
                    </Label>
                    <TimePicker
                      id="eventEndTime"
                      data-testid="event-end-time"
                      value={eventEndTime}
                      onChange={(val) => {
                        setEventEndTime(val);
                        setErrors(prev => ({ ...prev, eventEndTime: null }));
                      }}
                      className={cn(errors.eventEndTime && "[&_button]:border-red-500 [&_button]:border-2")}
                    />
                    {errors.eventEndTime && <p className="text-red-500 text-sm mt-1">{errors.eventEndTime}</p>}
                  </div>
                  <div>
                    <Label htmlFor="deliveryEarliest" className="text-charcoal/70 mb-2 block">
                      {t.booking.earliestDelivery} *
                    </Label>
                    <TimePicker
                      id="deliveryEarliest"
                      data-testid="delivery-earliest"
                      value={deliveryEarliest}
                      onChange={(val) => {
                        setDeliveryEarliest(val);
                        setErrors(prev => ({ ...prev, deliveryEarliest: null }));
                      }}
                      className={cn(errors.deliveryEarliest && "[&_button]:border-red-500 [&_button]:border-2")}
                    />
                    {errors.deliveryEarliest && <p className="text-red-500 text-sm mt-1">{errors.deliveryEarliest}</p>}
                  </div>
                  <div>
                    <Label htmlFor="pickupLatest" className="text-charcoal/70 mb-2 block">
                      {t.booking.latestPickup} *
                    </Label>
                    <TimePicker
                      id="pickupLatest"
                      data-testid="pickup-latest"
                      value={pickupLatest}
                      onChange={(val) => {
                        setPickupLatest(val);
                        setErrors(prev => ({ ...prev, pickupLatest: null }));
                      }}
                      className={cn(errors.pickupLatest && "[&_button]:border-red-500 [&_button]:border-2")}
                    />
                    {errors.pickupLatest && <p className="text-red-500 text-sm mt-1">{errors.pickupLatest}</p>}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Order Summary */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-white rounded-3xl border border-charcoal/10 p-6 shadow-sm">
                <h2 className="font-heading font-bold text-xl text-charcoal mb-6">
                  {t.booking.orderSummary}
                </h2>

                {getSelectedItemsCount() === 0 ? (
                  <p className="text-charcoal/60 text-center py-8">
                    {t.booking.noItemsSelected}
                  </p>
                ) : (
                  <>
                    <div className="space-y-3 mb-6">
                      {Object.entries(selectedItems)
                        .filter(([, qty]) => qty > 0)
                        .map(([productId, qty]) => {
                          const product = products.find(
                            (p) => p.id === productId
                          );
                          if (!product) return null;
                          return (
                            <div
                              key={productId}
                              className="flex justify-between text-sm"
                            >
                              <span className="text-charcoal/70">
                                {getProductName(product)} x {qty}
                              </span>
                              <span className="font-medium text-charcoal">
                                ${(product.price_per_day * qty).toFixed(2)}/day
                              </span>
                            </div>
                          );
                        })}
                    </div>

                    {startDate && endDate && (
                      <div className="border-t border-charcoal/10 pt-4 mb-6">
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-charcoal/70">Rental Period</span>
                          <span className="font-medium text-charcoal">
                            {getRentalDays()} days
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-charcoal/70">
                            {format(startDate, "MMM d")} -{" "}
                            {format(endDate, "MMM d, yyyy")}
                          </span>
                        </div>
                        {getSelectedItemsCount() > 0 && (
                          <div className="flex justify-between text-sm mt-2">
                            <span className="text-charcoal/70">
                              {t.booking.deliveryPickupFee}
                            </span>
                            <span className="font-medium text-charcoal">
                              ${Number(deliveryFee || 0).toFixed(2)}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="border-t border-charcoal/10 pt-4">
                      <div className="flex justify-between mb-6">
                        <span className="font-heading font-bold text-lg text-charcoal">
                          {t.booking.total}
                        </span>
                        <span className="font-heading font-bold text-2xl text-sage">
                          ${calculateTotal().toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </>
                )}

                <Button
                  type="submit"
                  data-testid="submit-booking-btn"
                  disabled={
                    submitting ||
                    getSelectedItemsCount() === 0 ||
                    !startDate ||
                    !endDate
                  }
                  className="w-full bg-sage hover:bg-sage/90 text-white rounded-full py-6 text-lg font-bold btn-hover"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      ...
                    </>
                  ) : (
                    t.booking.submitBooking
                  )}
                </Button>

                <p className="text-xs text-charcoal/60 text-center mt-4">
                  {t.booking.successMessage}
                </p>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Booking;
